import type { ApiEnv, ApiLocation } from "@/utils/enhance-request";
import LogoApexmove from "@/assets/brand-logo/logo-apexmove.png";
import LogoApexmoveT from "@/assets/brand-logo/logo-apexmove-t.png";
import LogoCarbinox from "@/assets/brand-logo/logo-carbinox.png";
import LogoLiedong from "@/assets/brand-logo/logo-liedong.png";
import LogoLiedongT from "@/assets/brand-logo/logo-liedong-td.png";
import LogoRyzemove from "@/assets/brand-logo/logo-ryzemove.png";
import LogoRyzemoveT from "@/assets/brand-logo/logo-ryzemove-t.png";
import { COMPANY_SETTINGS_CONTENT_MAP } from "./company-settings";

export const AMAZEMOVE_ENV = (process.env.AMAZEMOVE_ENV || "development") as ApiEnv;

// 将未知接口节点统一回退到 global，保证业务配置与构建模板使用同一品牌。
const requestedApiLocation = process.env.API_LOCATION;
export const API_LOCATION = (
  requestedApiLocation && requestedApiLocation in COMPANY_SETTINGS_CONTENT_MAP ? requestedApiLocation : "global"
) as ApiLocation;

// 暴露当前环境配置，便于部署后排查实际打包参数。
(window as any).__AMAZEMOVE_CONFIG = {
  env: AMAZEMOVE_ENV,
  apiLocation: API_LOCATION,
};

export const IS_DEV = AMAZEMOVE_ENV === "development" || AMAZEMOVE_ENV === "development2";
export const IS_TEST = AMAZEMOVE_ENV === "test";
export const IS_PROD = AMAZEMOVE_ENV === "production";
// export const WS_PATH = `ws://192.168.110.217:8207/websocket/user`;

// 上传存储节点按接口节点和运行环境配置，新增品牌时无需修改判断逻辑。
const FILE_UPLOAD_CODE_MAP: Record<ApiLocation, Record<ApiEnv, string>> = {
  global: {
    development: "aliyun",
    development2: "aliyun",
    test: "aliyun",
    pre: "amazon",
    production: "amazon",
  },
  hk: {
    development: "aliyun",
    development2: "aliyun",
    test: "aliyun",
    pre: "aliyun",
    production: "aliyun",
  },
  cn: {
    development: "aliyun",
    development2: "aliyun",
    test: "aliyun",
    pre: "amazon",
    production: "amazon",
  },
  cm: {
    development: "aliyun",
    development2: "aliyun",
    test: "aliyun",
    pre: "amazon",
    production: "amazon",
  },
  rm: {
    development: "aliyun",
    development2: "aliyun",
    test: "aliyun",
    pre: "amazon",
    production: "amazon",
  },
};

export const FILE_UPLOAD_CODE = FILE_UPLOAD_CODE_MAP[API_LOCATION][AMAZEMOVE_ENV];

export const GLOBAL_KEYS = {
  USER_INFO: "_uis",
  USER_TOKEN: "token",
  TOKEN_EXPIRE: "_te",
  PLAT_THEME: "_pt",
  API_LOCATION: "_al",
};

type CompanySettings = {
  title: string;
  slogan: string;
  copyright: string;
  favicon: string;
  logo: string;
  logoText: string;
  loginDescriptionKey: string;
  mapSupportWatchKey: string;
  mapSupportedDevices: {
    connected: string;
    default: string;
  };
  showMapTutorialVideos: boolean;
  appDownloadUrl: string;
};

const GLOBAL_PAGE_SETTINGS = {
  loginDescriptionKey: "login_description",
  mapSupportWatchKey: "map_support_watch",
  mapSupportedDevices: {
    connected: "TANK T4 SE, TANK M4 SE",
    default: "TANK T4, TANK M4",
  },
  showMapTutorialVideos: true,
};

// 生成与 HTML favicon 一致的公开品牌图片地址。
const getBrandFavicon = (fileName: string) => `${process.env.PUBLIC_URL || ""}/brand-logo/${fileName}`;

// 每个接口节点维护完整页面配置，新增品牌时无需在组件中增加环境分支。
const COMPANY_SETTINGS_MAP = {
  global: {
    ...COMPANY_SETTINGS_CONTENT_MAP.global,
    favicon: getBrandFavicon(COMPANY_SETTINGS_CONTENT_MAP.global.favicon),
    logo: LogoApexmove,
    logoText: LogoApexmoveT,
    appDownloadUrl: "https://hybrid.amazwear.com/app-download",
    ...GLOBAL_PAGE_SETTINGS,
  },
  hk: {
    ...COMPANY_SETTINGS_CONTENT_MAP.hk,
    favicon: getBrandFavicon(COMPANY_SETTINGS_CONTENT_MAP.hk.favicon),
    logo: LogoApexmove,
    logoText: LogoApexmoveT,
    appDownloadUrl: "https://hybrid-hk.amazwear.com/app-download",
    ...GLOBAL_PAGE_SETTINGS,
  },
  cn: {
    ...COMPANY_SETTINGS_CONTENT_MAP.cn,
    favicon: getBrandFavicon(COMPANY_SETTINGS_CONTENT_MAP.cn.favicon),
    logo: LogoLiedong,
    logoText: LogoLiedongT,
    loginDescriptionKey: "login_description_cn",
    mapSupportWatchKey: "map_support_watch_cn",
    mapSupportedDevices: {
      connected: "TANK T4 SE, TANK M4 SE",
      default: "猎险者 AD10 , 猎险者 EP10 ",
    },
    showMapTutorialVideos: false,
    appDownloadUrl: "https://hybrid.apexmove.com/app-download",
  },
  cm: {
    ...COMPANY_SETTINGS_CONTENT_MAP.cm,
    favicon: getBrandFavicon(COMPANY_SETTINGS_CONTENT_MAP.cm.favicon),
    logo: LogoCarbinox,
    logoText: LogoCarbinox,
    appDownloadUrl: "",
    ...GLOBAL_PAGE_SETTINGS,
  },
  rm: {
    ...COMPANY_SETTINGS_CONTENT_MAP.rm,
    favicon: getBrandFavicon(COMPANY_SETTINGS_CONTENT_MAP.rm.favicon),
    logo: LogoRyzemove,
    logoText: LogoRyzemoveT,
    appDownloadUrl: "",
    ...GLOBAL_PAGE_SETTINGS,
  },
} satisfies Record<ApiLocation, CompanySettings>;

export const COMPANY_SETTINGS = COMPANY_SETTINGS_MAP[API_LOCATION];
